/**
 * Getting Started Watch page — private, auth-only.
 *
 * Renders every video in the "Getting Started" YouTube playlist inside an
 * embedded YouTube IFrame Player. As the user crosses the 60% mark on a
 * video, the client pings `POST /api/watch-credit/getting-started` and
 * the backend marks that video watched. Once every video is watched, the
 * backend awards a one-time 10,000 Step-a-Thon point bonus.
 *
 * The playlist_id is stored in `db.app_settings` (admin-settable). If it
 * isn't set yet, this page renders a friendly empty state instead of a
 * broken player.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle, Sparkles, Youtube, Gift, ArrowLeft } from "lucide-react";
import API from "@/lib/api";
import { analytics } from "@/instrument";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Loads the YouTube IFrame Player API script exactly once.
let _ytApiPromise = null;
function loadYouTubeApi() {
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") try { prev(); } catch { /* ignore */ }
      resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
  });
  return _ytApiPromise;
}

function VideoCard({ videoId, watched, threshold, onCredited }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const sentRef = useRef(false); // dedupe — one POST per mounted card
  const [localWatched, setLocalWatched] = useState(watched);

  useEffect(() => { setLocalWatched(watched); }, [watched]);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !containerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onStateChange: (e) => {
            // 1 = playing → start polling. Everything else → stop.
            if (e.data === 1) {
              if (pollRef.current) return;
              pollRef.current = setInterval(async () => {
                try {
                  const t = playerRef.current?.getCurrentTime?.() || 0;
                  const d = playerRef.current?.getDuration?.() || 0;
                  if (!d || sentRef.current || localWatched) return;
                  if (t / d >= threshold) {
                    sentRef.current = true;
                    const { data } = await API.post("/watch-credit/getting-started", {
                      video_id: videoId,
                      watched_seconds: t,
                      total_seconds: d,
                    });
                    if (data?.credited_video) {
                      setLocalWatched(true);
                      analytics.capture("video_watched", { video_id: videoId, threshold });
                      onCredited?.(videoId, data);
                    }
                  }
                } catch { /* swallow — retries fire on next tick */ }
              }, 4000);
            } else if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      try { playerRef.current?.destroy?.(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, threshold]);

  return (
    <div className="relative bg-slate-900 rounded-2xl overflow-hidden shadow-lg border border-slate-800" data-testid={`watch-card-${videoId}`}>
      {localWatched && (
        <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-lg" data-testid={`watch-badge-${videoId}`}>
          <CheckCircle className="w-3.5 h-3.5" /> Watched
        </div>
      )}
      {/* 16:9 responsive box */}
      <div className="relative w-full pb-[56.25%]">
        <div ref={containerRef} className="absolute inset-0" />
      </div>
    </div>
  );
}

export default function GettingStartedWatchPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authErr, setAuthErr] = useState(false);

  const load = () => {
    setLoading(true);
    API.get("/watch-credit/getting-started")
      .then((r) => { setStatus(r.data); setAuthErr(false); })
      .catch((e) => {
        if (e?.response?.status === 401) setAuthErr(true);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const watchedSet = useMemo(
    () => new Set((status?.watched_video_ids) || []),
    [status]
  );

  const onCredited = (_vid, data) => {
    if (data?.awarded_now) {
      toast.success(`🎉 +${data.reward_points.toLocaleString()} Step-a-Thon points!`, {
        description: "You've finished the Getting Started series.",
        duration: 8000,
      });
    } else {
      toast.success("Video complete — keep going!", { duration: 2500 });
    }
    // Refresh so watched badges & remaining count stay accurate.
    load();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    );
  }
  if (authErr) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <p className="text-slate-700 mb-4">Sign in to unlock the Getting Started reward.</p>
        <Button onClick={() => navigate("/login?next=/watch/getting-started")}>Sign in</Button>
      </div>
    );
  }

  const enabled = !!status?.enabled;
  const videos = status?.video_ids || [];
  const watchedCount = watchedSet.size;
  const total = videos.length;
  const alreadyCredited = !!status?.already_credited;
  const rewardPoints = status?.reward_points ?? 10000;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="watch-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-700 via-fuchsia-700 to-pink-600 p-6 sm:p-8 shadow-xl">
          <Sparkles aria-hidden="true" className="absolute top-4 right-6 w-6 h-6 text-fuchsia-100/70 animate-sparkle-float-slow" />
          <Sparkles aria-hidden="true" className="absolute bottom-4 left-6 w-4 h-4 text-violet-100/70 animate-sparkle-spin" style={{ animationDelay: "0.5s" }} />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white mb-3">
              <Gift className="w-3.5 h-3.5" /> One-time bonus
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Watch the Getting Started series →<br />
              earn <span className="text-yellow-300">{rewardPoints.toLocaleString()}</span> Step-a-Thon points
            </h1>
            <p className="mt-2 text-sm sm:text-base text-purple-100">
              Play each video below. As long as you reach at least {Math.round((status?.threshold_pct || 0.6) * 100)}% of every video, we&apos;ll drop the bonus into your rewards balance the moment you finish the last one.
            </p>
            {enabled && (
              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-white/20 overflow-hidden">
                  <div className="h-full bg-yellow-300 transition-all duration-500"
                       style={{ width: `${total ? (watchedCount / total) * 100 : 0}%` }}
                       data-testid="watch-progress-bar" />
                </div>
                <span className="text-xs font-bold text-white tabular-nums" data-testid="watch-progress-count">
                  {watchedCount} / {total}
                </span>
              </div>
            )}
            {alreadyCredited && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-1.5 text-sm font-bold text-white" data-testid="watch-already-credited">
                <CheckCircle className="w-4 h-4" /> Bonus already claimed — thanks for watching!
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        {!enabled ? (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="watch-empty-state">
            <Youtube className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-800">Getting Started series is coming soon</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
              An admin still needs to link the YouTube playlist. Check back shortly.
            </p>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {videos.map((vid) => (
              <VideoCard
                key={vid}
                videoId={vid}
                watched={watchedSet.has(vid)}
                threshold={status?.threshold_pct || 0.6}
                onCredited={onCredited}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
